import React, { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material'
import { CheckCircle, ErrorOutline, Inventory2, LocalShipping, Refresh } from '@mui/icons-material'
import { apiService } from '../services/api'
import type { PalworldSupplyPendente } from '../types'

const ItensPendentesEntregaPage: React.FC = () => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [data, setData] = useState<PalworldSupplyPendente[]>([])

  const [delivering, setDelivering] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [dialogSeverity, setDialogSeverity] = useState<'success' | 'error'>('success')
  const [dialogMessage, setDialogMessage] = useState('')

  const load = async () => {
    setLoading(true)
    setError(null)
    try {
      const rows = await apiService.getPalworldSupplyPendentes()
      setData(Array.isArray(rows) ? rows : [])
    } catch (e: any) {
      setError(e?.message || 'Erro ao consultar itens pendentes.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    let mounted = true
    const run = async () => {
      setLoading(true)
      setError(null)
      try {
        const rows = await apiService.getPalworldSupplyPendentes()
        if (!mounted) return
        setData(Array.isArray(rows) ? rows : [])
      } catch (e: any) {
        if (!mounted) return
        setError(e?.message || 'Erro ao consultar itens pendentes.')
      } finally {
        if (!mounted) return
        setLoading(false)
      }
    }
    void run()
    return () => {
      mounted = false
    }
  }, [])

  const summary = useMemo(() => {
    const totalItems = data.length
    const totalQuantity = data.reduce((acc, r) => acc + (Number(r.quantity ?? 0) || 0), 0)
    return { totalItems, totalQuantity }
  }, [data])

  const handleDeliverClick = () => {
    if (delivering || loading || data.length === 0) return
    setConfirmOpen(true)
  }

  const handleDeliverConfirm = async () => {
    if (delivering) return
    setDelivering(true)
    setConfirmOpen(false)
    try {
      await apiService.entregarPalworldSupplyPendentes()
      setDialogSeverity('success')
      setDialogMessage('Itens pendentes entregues com sucesso.')
      setDialogOpen(true)
      await load()
    } catch (e: any) {
      setDialogSeverity('error')
      setDialogMessage(e?.message || 'Erro ao entregar itens pendentes.')
      setDialogOpen(true)
    } finally {
      setDelivering(false)
    }
  }

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 3, md: 5 } }}>
      <Stack spacing={2.5}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h4" sx={{ fontWeight: 900, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Inventory2 />
              Itens pendentes de entrega
            </Typography>
            <Typography sx={{ color: 'text.secondary' }}>
              Lista de itens comprados que ainda não foram entregues no Palworld.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              startIcon={<LocalShipping />}
              onClick={handleDeliverClick}
              disabled={loading || delivering || data.length === 0}
              sx={{ fontWeight: 900 }}
            >
              {delivering ? 'Entregando…' : 'Entregar itens pendentes'}
            </Button>
            <Button variant="outlined" startIcon={<Refresh />} onClick={load} disabled={loading || delivering}>
              Atualizar
            </Button>
          </Stack>
        </Box>

        {error && <Alert severity="error">{error}</Alert>}

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent sx={{ p: 2.5 }}>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Itens
                </Typography>
                <Typography sx={{ fontWeight: 900, fontSize: 22 }}>{summary.totalItems.toLocaleString('pt-BR')}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent sx={{ p: 2.5 }}>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Quantidade total
                </Typography>
                <Typography sx={{ fontWeight: 900, fontSize: 22 }}>{summary.totalQuantity.toLocaleString('pt-BR')}</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
          <Box sx={{ p: 2.5, borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
            <Typography variant="h6" sx={{ fontWeight: 900 }}>
              Pendências
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Itens pendentes de entrega para sua conta.
            </Typography>
          </Box>

          {loading && data.length === 0 ? (
            <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell width={64} sx={{ fontWeight: 900 }}>
                      Imagem
                    </TableCell>
                    <TableCell sx={{ fontWeight: 900 }}>Item</TableCell>
                    <TableCell sx={{ fontWeight: 900 }} align="right">
                      Quantidade
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
                        Nenhum item pendente.
                      </TableCell>
                    </TableRow>
                  ) : (
                    data.map((row) => (
                      <TableRow key={row.supplyMongoId || row.supplyId} hover>
                        <TableCell>
                          <Avatar
                            variant="rounded"
                            src={row.imageUrl ?? undefined}
                            alt={row.item}
                            sx={{ width: 44, height: 44, bgcolor: 'rgba(255,255,255,0.08)' }}
                            imgProps={{ loading: 'lazy' }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 800 }}>
                            {row.item || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 900 }}>
                          {(Number(row.quantity ?? 0) || 0).toLocaleString('pt-BR')}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Stack>

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { bgcolor: '#101225', border: '1px solid rgba(255,255,255,0.10)' } }}
      >
        <DialogTitle sx={{ fontWeight: 900, display: 'flex', alignItems: 'center', gap: 1 }}>
          {dialogSeverity === 'success' ? <CheckCircle color="success" /> : <ErrorOutline color="error" />}
          {dialogSeverity === 'success' ? 'Sucesso' : 'Erro'}
        </DialogTitle>
        <DialogContent dividers>
          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              bgcolor: dialogSeverity === 'success' ? 'rgba(0,230,118,0.10)' : 'rgba(244,67,54,0.10)',
              border: dialogSeverity === 'success' ? '1px solid rgba(0,230,118,0.25)' : '1px solid rgba(244,67,54,0.25)'
            }}
          >
            <Typography sx={{ fontWeight: 900, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {dialogMessage}
            </Typography>
            {dialogSeverity === 'error' && (
              <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.75 }}>
                Verifique se você está logado no jogo e tente novamente.
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            variant="contained"
            onClick={() => setDialogOpen(false)}
            sx={{
              fontWeight: 900,
              ...(dialogSeverity === 'success'
                ? { background: 'linear-gradient(45deg, #00e676 30%, #4caf50 90%)' }
                : {})
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmOpen}
        onClose={() => (delivering ? null : setConfirmOpen(false))}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { bgcolor: '#101225', border: '1px solid rgba(255,255,255,0.10)' } }}
      >
        <DialogTitle sx={{ fontWeight: 900, display: 'flex', alignItems: 'center', gap: 1 }}>
          <LocalShipping sx={{ color: 'primary.main' }} />
          Confirmar entrega
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.25}>
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: 'rgba(33,150,243,0.10)',
                border: '1px solid rgba(33,150,243,0.25)'
              }}
            >
              <Typography sx={{ fontWeight: 900 }}>
                Para realizar a entrega, você precisa estar logado no jogo. Deseja continuar?
              </Typography>
              {data.length > 0 && (
                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.75 }}>
                  Pendências: {summary.totalItems.toLocaleString('pt-BR')} item(ns) • {summary.totalQuantity.toLocaleString('pt-BR')} unidade(s)
                </Typography>
              )}
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button variant="outlined" onClick={() => setConfirmOpen(false)} disabled={delivering}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleDeliverConfirm}
            disabled={delivering}
            sx={{ fontWeight: 900, background: 'linear-gradient(45deg, #00e676 30%, #4caf50 90%)' }}
          >
            Continuar
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  )
}

export default ItensPendentesEntregaPage
